import { omit } from 'lodash';
import { UpgradePlanQuote } from 'admin/components/AdminUpgradePlan/AdminUpgradePlanContainer';
import { UpdateQuote_updateQuote_quote } from 'admin/mutations/__generated__/UpdateQuote';
import { regex } from 'lib/assertions';
import errorLogger from 'utils/errorLogger';

type UpdateQuote = UpdateQuote_updateQuote_quote;

export function trimWhitespaceOnStrings(field: any) {
  if (typeof field === 'string' || field instanceof String) {
    return field.trim();
  }
  return field;
}

export function conditionallyAddPlus(number: string): string {
  if (number && number.length > 0 && !number.startsWith('+')) {
    return '+' + number;
  } else {
    return number;
  }
}

export function priceInCents(price: string) {
  return parseFloat(price.replace(/[,$.]/g, ''));
}

export function badPostalCodeForCountry(postalCode?: string, country?: string) {
  // do not flag an error if either field is blank
  if (!postalCode || postalCode === '' || !country || country === '') {
    return false;
  }

  switch (country) {
    case 'US':
      return !new RegExp(regex.usZipcode).test(postalCode || '');

    case 'CA':
      return !new RegExp(regex.caZipcode).test(postalCode || '');

    default:
      return false;
  }
}
// Filters __typename keys out of quote response so they can be used
// to generate inputs for subsequent quote updates
export function filterQuote(quote: UpdateQuote): UpgradePlanQuote {
  const {
    endDate,
    expirationDate,
    organization,
    organizationAddress,
    price,
    shareToken,
    startDate,
    studentsCount,
    tax,
    taxExempt,
    teachersCount,
    total,
    uuid,
  } = quote;

  const filteredOrganization = {
    id: organization.id,
    uuid: organization.uuid,
    name: organization.name,
    city: organization.city || '',
    state: organization.state || '',
  };

  return {
    endDate,
    expirationDate,
    organization: filteredOrganization,
    organizationAddress: omit(organizationAddress, '__typename'),
    price,
    shareToken,
    startDate,
    studentsCount,
    tax,
    taxExempt,
    teachersCount,
    total,
    uuid,
  };
}

// returns an empty quote object for when one is required but there's nothing to put in it
export function getBlankQuote(): UpgradePlanQuote {
  return {
    uuid: '',
    studentsCount: 0,
    teachersCount: 0,
    startDate: '',
    endDate: '',
    taxExempt: false,
    price: '',
    tax: '',
    total: '',
    expirationDate: '',
    shareToken: '',
    organization: {
      id: 0,
      uuid: '',
      name: '',
      city: '',
      state: '',
    },
    organizationAddress: {
      streetAddress: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
    },
  };
}

export function shouldStripeErrorBeReported(error: stripe.Error) {
  const ignoreErrorTypes = ['api_connection_error', 'card_error'];
  const ignoreTypes = ['incomplete_zip', 'incorrect_number', 'incorrect_zip', 'incorrect_cvc', 'invalid_expiry_year', 'invalid_expiry_month'];
  return !ignoreErrorTypes.includes(error.type) && !ignoreTypes.includes(error.code || '')
}

export function reportStripeErrorToRollbar(customErrorText: string, error: stripe.Error) {
  errorLogger(
    `${customErrorText} Error: ${error.type} - ${error.message}`
  )({
    code: error?.code,
    message: error?.message,
    err: error,
  });
}
